/**
 * The arithmetic behind a paged list, with no React in it.
 *
 * Offset paging is four lines of sums and every one of them is an off-by-one
 * waiting to happen: the last page is short, the count line is 1-based while
 * the offset is 0-based, and "next" must stop at the end rather than walking
 * past it into an empty page. Six screens each doing it inline is six chances,
 * and the failure is quiet — a list that renders, with a row missing or a
 * button that does nothing.
 *
 * Separated from the component so the sums are unit-testable without rendering
 * anything. See `docs/ui-conventions.md`, "Reading a list", for *when* to page
 * at all: most back-office sets are read whole through `collectPages` and want
 * none of this.
 */

/**
 * Rows per page.
 *
 * The API's own default is 50 and its documented maximum is 200. Deliberately
 * not `collect-pages.ts`'s `PAGE_SIZE` of 100: that one is sized to make the
 * read-whole loop a single round trip, and this one is sized to be read by a
 * person on a screen. They answer different questions and coupling them would
 * mean a display change quietly altering how many requests a different screen
 * makes.
 */
export const ROWS_PER_PAGE = 50;

/** Where a list is: the window asked for, and how many rows matched it. */
export interface PageState {
  readonly offset: number;
  readonly limit: number;
  readonly total: number;
}

/**
 * The window after moving one page, clamped at both ends.
 *
 * Clamping rather than refusing, because the caller that most needs this is a
 * filter change: a list sitting at offset 200 that suddenly matches 12 rows
 * must land somewhere real, not on an empty page that reads as "no results".
 */
export function pageAfter({ offset, limit, total }: PageState, step: 1 | -1): number {
  const moved = offset + step * limit;
  if (moved < 0) return 0;

  // The first offset of the last page. `total` of 0 and a total that divides
  // exactly both land on a valid first row rather than one page past the end.
  const lastPageOffset = total === 0 ? 0 : Math.floor((total - 1) / limit) * limit;
  return Math.min(moved, lastPageOffset);
}

export const hasPreviousPage = ({ offset }: PageState): boolean => offset > 0;

export const hasNextPage = ({ offset, limit, total }: PageState): boolean => offset + limit < total;

/**
 * The count line, e.g. `51–100 of 240`.
 *
 * 1-based and inclusive, because it is read by a person rather than by an
 * index. An en dash, not a hyphen: it is a range.
 *
 * A short last page is the case worth having a name for — `describePage` reads
 * the *rows actually present*, not `offset + limit`, so the final page of 240
 * says `201–240` and never `201–250 of 240`.
 */
export function describePage({ offset, limit, total }: PageState): string {
  if (total === 0) return 'No rows';

  const first = offset + 1;
  const last = Math.min(offset + limit, total);
  return `${first}–${last} of ${total}`;
}
